/**
 * home/sections/SourcesSection.tsx
 * Sección Sources — DS v2 (H.06)
 *
 * @version 2.5.0 — sin sondeos a las APIs externas (`PODASRC-c`, 28-oct-2026): el `HEAD no-cors`
 *   devolvía siempre una respuesta opaca y el punto salía «Operativa» aunque la ruta no existiera.
 *          2.4.0 — leyenda con tercer estado híbrido (naranja):
 *   · Badge tier R2 → «Copia de PTEL Tool · Cloudflare»
 *   · Pill modePill por card ('Servidor PTEL' | 'API en línea' | 'Híbrido')
 *   · Catastro R2 marcado como 'Híbrido' (R2 local + RC en tiempo real)
 *   · Catastro API como entrada separada en fallback (19 fuentes / 5 APIs)
 *   · Leyenda inferior con tres estados documentados
 */
import type { FC } from 'react';
import { SOURCES, TIER_ORDER } from '../data';

// ── Config visual por tier ─────────────────────────────────────────────
const TIER_CONFIG: Record<string, {
  label: string; badgeClass: string; badgeText: string;
}> = {
  masivas:        { label: 'Nivel 1 — Fuentes masivas',       badgeClass: 'tier-r2',  badgeText: 'Copia de PTEL Tool · Cloudflare' },
  sectoriales:    { label: 'Nivel 2 — Sectoriales DERA',       badgeClass: 'tier-r2',  badgeText: 'Copia de PTEL Tool · Cloudflare' },
  patrimoniales:  { label: 'Nivel 3 — Patrimonio cultural',    badgeClass: 'tier-r2',  badgeText: 'Copia de PTEL Tool · Cloudflare' },
  especializadas: { label: 'Nivel 4 — Fuentes especializadas', badgeClass: 'tier-r2',  badgeText: 'Copia de PTEL Tool · Cloudflare' },
  fallback:       { label: 'Respaldo — APIs en línea',         badgeClass: 'tier-api', badgeText: 'Consulta activa · En línea'  },
};

// ── Componente principal ───────────────────────────────────────────────
export const SourcesSection: FC = () => {
  return (
    <section className="sources-section" id="sources">
      <div className="sources-inner">

        <div className="sources-head">
          <div className="sources-eyebrow">Fuentes de datos</div>
          <h2 className="sources-title">14 bases de datos y servicios en línea de respaldo</h2>
          <p className="sources-subtitle">
            14 bases de datos preparadas por PTEL Tool y alojadas en Cloudflare, más servicios en línea de respaldo, entre ellos OpenStreetMap.
          </p>
        </div>

        {TIER_ORDER.map((tier) => {
          const tierSources = SOURCES.filter((s) => s.tier === tier);
          if (tierSources.length === 0) return null;
          const cfg = TIER_CONFIG[tier] ?? { label: tier, badgeClass: 'tier-r2', badgeText: '' };
          return (
            <div key={tier} className="sources-tier">
              <div className="sources-tier-label">
                <span className="sources-tier-name">{cfg.label}</span>
                <span className="sources-tier-line" />
                <span className={`sources-tier-badge ${cfg.badgeClass}`}>{cfg.badgeText}</span>
              </div>
              <div className="sources-grid">
                {tierSources.map((source) => {
                  return (
                    <div
                      key={source.name}
                      className={`sources-card${
                        source.modePill === 'Servidor PTEL' ? ' sources-card--ptel'
                        : source.modePill === 'API en línea' ? ' sources-card--api'
                        : source.modePill === 'Híbrido'     ? ' sources-card--hybrid'
                        : ''
                      }`}
                    >
                      {source.modePill && (
                        <span className={`sources-mode-pill sources-mode-pill--${
                          source.modePill === 'Servidor PTEL' ? 'ptel'
                          : source.modePill === 'API en línea' ? 'api'
                          : 'hybrid'
                        }`}>
                          <span className="sources-mode-dot" />
                          {source.modePill}
                        </span>
                      )}
                      <div className="sources-card-top">
                        <div className="sources-card-name">{source.name}</div>
                        <div className="sources-card-top-right">
                          <span className="sources-card-count">{source.badge}</span>
                        </div>
                      </div>
                      <p className="sources-card-desc">{source.desc}</p>
                      {source.note && <p className="sources-card-note">{source.note}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Leyenda */}
        <div className="sources-legend">
          <div className="sources-legend-item">
            <span className="sources-legend-dot sources-legend-dot--ptel" />
            Copia de PTEL Tool en Cloudflare — se descarga de ahí, sin consultar al organismo de origen
          </div>
          <div className="sources-legend-item">
            <span className="sources-legend-dot sources-legend-dot--hybrid" />
            Fuente híbrida — copia de PTEL Tool más consulta de referencia catastral en tiempo real
          </div>
          <div className="sources-legend-item">
            <span className="sources-legend-dot sources-legend-dot--api" />
            API en línea — consulta activa en tiempo real a servicios externos
          </div>
        </div>

      </div>
    </section>
  );
};

export default SourcesSection;
